import React, { useState, useRef, useEffect } from 'react';
import Tooltip from '@mui/material/Tooltip';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsersLine } from '@fortawesome/free-solid-svg-icons';
import { t } from 'i18next';

// ── Types ─────────────────────────────────────────────────────────────────────

export type Role = {
  id: number; title: string; color?: string; isDefault?: boolean; description?: number | null;
  equipment_add: number | null; equipment_delete: number | null;
  equipment_edit: number | null; equipment_export: number | null;
  equipment_inventory_mode: number | null; equipment_pat_test: number | null;
  equipment_view_all: number | null; equipment_view_self_response: number | null;
  equipment_settings: number | null;
  expenses_user: number | null; expenses_manager: number | null;
  expenses_admin: number | null; expenses_settings: number | null;
  income_user: number | null; income_manager: number | null; income_admin: number | null;
  expenses_view: number | null; expenses_add: number | null; expenses_edit: number | null;
  expenses_delete: number | null; expenses_approve: number | null;
  income_view: number | null; income_add: number | null; income_edit: number | null;
  income_delete: number | null; income_approve: number | null;
  car_exp_view: number | null; car_exp_add: number | null; car_exp_edit: number | null;
  car_exp_delete: number | null; car_exp_approve: number | null;
  payroll_view: number | null; payroll_add: number | null; payroll_edit: number | null;
  payroll_delete: number | null; payroll_approve: number | null;
  timesheet_view_all: number | null; timesheet_view_dep: number | null;
  timesheet_view_sv: number | null;
  timesheet_template: number | null;
  timesheet_return_week: number | null; timesheet_settings: number | null;
  dashboard_enabled: number | null;
  timesheets_enabled: number | null;
  timesheet_enabled: number | null;
  timesheet_approve_enabled: number | null; timesheet_approve_view_sv: number | null;
  timesheet_approve_sv: number | null; timesheet_approve_view_all: number | null;
  timesheet_approve_all: number | null; timesheet_approve_view_dep: number | null;
  timesheet_approve_dep: number | null;
  timesheet_reports_enabled: number | null; timesheet_reports_view_dep: number | null;
  timesheet_reports_view_sv: number | null; timesheet_reports_view_all: number | null;
  timesheet_reports_view_own: number | null; timesheet_reports_export: number | null;
  document_view: number | null; document_add: number | null;
  booking: number | null; booking_enabled: number | null; clients: number | null; orders: number | null;
  finance: number | null; finance_enabled: number | null; settings_view: number | null;
  users_view: number | null; users_view_sub: number | null;
  users_view_all: number | null; users_add: number | null;
  users_delete: number | null; users_edit: number | null;
  toil_view_own: number | null; toil_edit_all: number | null;
  toil_view_sv: number | null; toil_view_dep: number | null; toil_view_all: number | null;
  crm_contacts_get: number | null; crm_contacts_add: number | null;
  crm_contacts_edit: number | null; crm_contacts_delete: number | null;
  crm_contacts_export: number | null; crm_organisations_get: number | null;
  crm_organisations_manage: number | null; crm_projects_get: number | null;
  crm_projects_manage: number | null; crm_admin: number | null;
  community_view: number | null; community_sessions_manage: number | null;
  community_indicators_view: number | null; community_indicators_manage: number | null;
  community_reports: number | null; community_surveys_manage: number | null;
  training_view: number | null; training_admin: number | null;
  dashboard_settings: number | null; dashboard_widget_feed_enabled: number | null; dashboard_edit_own: number | null;
  docs_enabled: number | null; crm_enabled: number | null;
  tasks_enabled: number | null;
  community_enabled: number | null; equipment_enabled: number | null;
  hr_enabled: number | null; training_enabled: number | null;
  settings_enabled: number | null; orders_enabled: number | null;
  toil_enabled: number | null;
  hr_absence_view: number | null; hr_absence_view_sv: number | null; hr_absence_view_team: number | null; hr_absence_view_all: number | null;
  hr_absence_approve: number | null; hr_absence_approve_team: number | null; hr_absence_approve_all: number | null;
  hr_overview_events: number | null;
  hr_leave_view_own: number | null;
  hr_employees_enabled: number | null; hr_employees_edit: number | null;
  hr_org_enabled: number | null;
  hr_settings: number | null;
  [key: string]: any;
};

interface Props {
  role: Role[]; onRoleChange?: () => void;
  handleShowDelete: any; showDelete: any; setShowDelete: any;
  refreshUserHeder: any; selectedRoleDelete: any;
  handleOpenAdd: any; refreshRoleAccept: any;
  canEdit?: boolean;
}

// ── Permission groups ─────────────────────────────────────────────────────────

export const GROUPS: { name: string; emoji: string; keys: { key: keyof Role; label: string; section?: string; isMaster?: boolean; isSectionMaster?: boolean; type?: 'numeric' }[] }[] = [
  { name: 'Dashboard', emoji: '🏠', keys: [
    { key: 'dashboard_enabled',              label: 'Enabled',         isMaster: true },
    { key: 'dashboard_settings',             label: 'Dashboard settings', section: 'Settings' },
    { key: 'dashboard_edit_own',             label: 'Customise own dashboard', section: 'Settings' },
    { key: 'dashboard_widget_feed_enabled',  label: 'Feed widget',     section: 'Widgets' },
  ]},
  { name: 'Timesheets', emoji: '⏱', keys: [
    { key: 'timesheets_enabled', label: 'Enabled', isMaster: true },
    // Timesheet
    { key: 'timesheet_enabled',         label: 'Timesheet',           section: 'Timesheet', isSectionMaster: true },
    { key: 'timesheet_view_sv',         label: 'View Supervised',     section: 'Timesheet' },
    { key: 'timesheet_view_dep',        label: 'View Department',     section: 'Timesheet' },
    { key: 'timesheet_view_all',        label: 'View All',            section: 'Timesheet' },
    { key: 'timesheet_template',        label: 'Templates',           section: 'Timesheet' },
    { key: 'timesheet_return_week',     label: 'Return to Draft',     section: 'Timesheet' },
    { key: 'timesheet_settings',        label: 'Settings',            section: 'Timesheet' },
    // Approve
    { key: 'timesheet_approve_enabled',    label: 'Approve',              section: 'Approve', isSectionMaster: true },
    { key: 'timesheet_approve_view_sv',    label: 'View Supervised',      section: 'Approve' },
    { key: 'timesheet_approve_view_dep',   label: 'View Department',      section: 'Approve' },
    { key: 'timesheet_approve_view_all',   label: 'View All',             section: 'Approve' },
    { key: 'timesheet_approve_sv',         label: 'Approve Supervised',   section: 'Approve' },
    { key: 'timesheet_approve_dep',        label: 'Approve Department',   section: 'Approve' },
    { key: 'timesheet_approve_all',        label: 'Approve All',          section: 'Approve' },
    // Reports
    { key: 'timesheet_reports_enabled',    label: 'Reports',              section: 'Reports', isSectionMaster: true },
    { key: 'timesheet_reports_view_own',   label: 'View Own',             section: 'Reports' },
    { key: 'timesheet_reports_view_sv',    label: 'View Supervised',      section: 'Reports' },
    { key: 'timesheet_reports_view_dep',   label: 'View Department',      section: 'Reports' },
    { key: 'timesheet_reports_view_all',   label: 'View All',             section: 'Reports' },
    { key: 'timesheet_reports_export',     label: 'Export',               section: 'Reports' },
    // TOIL
    { key: 'toil_enabled',       label: 'TOIL',             section: 'TOIL', isSectionMaster: true },
    { key: 'toil_view_own',      label: 'View Own',         section: 'TOIL' },
    { key: 'toil_view_sv',       label: 'View Supervised',  section: 'TOIL' },
    { key: 'toil_view_dep',      label: 'View Department',  section: 'TOIL' },
    { key: 'toil_view_all',      label: 'View All',         section: 'TOIL' },
    { key: 'toil_edit_all',      label: 'Edit All',         section: 'TOIL' },
  ]},
  { name: 'Documents', emoji: '📁', keys: [
    { key: 'docs_enabled',   label: 'Enabled', isMaster: true },
    { key: 'document_view',  label: 'View',    section: 'Documents', isSectionMaster: true },
    { key: 'document_add',   label: 'Add',     section: 'Documents' },
  ]},
  { name: 'CRM', emoji: '📋', keys: [
    { key: 'crm_enabled', label: 'Enabled', isMaster: true },
    // Contacts
    { key: 'crm_contacts_get',    label: 'View',   section: 'Contacts', isSectionMaster: true },
    { key: 'crm_contacts_add',    label: 'Add',    section: 'Contacts' },
    { key: 'crm_contacts_edit',   label: 'Edit',   section: 'Contacts' },
    { key: 'crm_contacts_delete', label: 'Delete', section: 'Contacts' },
    { key: 'crm_contacts_export', label: 'Export', section: 'Contacts' },
    // Organisations
    { key: 'crm_organisations_get',    label: 'View',   section: 'Organisations', isSectionMaster: true },
    { key: 'crm_organisations_manage', label: 'Manage', section: 'Organisations' },
    // Projects
    { key: 'crm_projects_get',    label: 'View',   section: 'Projects', isSectionMaster: true },
    { key: 'crm_projects_manage', label: 'Manage', section: 'Projects' },
    // Admin
    { key: 'crm_admin', label: 'Admin' },
  ]},
  { name: 'Tasks', emoji: '✅', keys: [
    { key: 'tasks_enabled', label: 'Enabled', isMaster: true },
  ]},
  { name: 'Community', emoji: '🏘', keys: [
    { key: 'community_enabled', label: 'Enabled', isMaster: true },
    // Sessions
    { key: 'community_view',             label: 'View',    section: 'Sessions', isSectionMaster: true },
    { key: 'community_sessions_manage',  label: 'Manage',  section: 'Sessions' },
    // Indicators
    { key: 'community_indicators_view',   label: 'View',   section: 'Indicators', isSectionMaster: true },
    { key: 'community_indicators_manage', label: 'Manage', section: 'Indicators' },
    // Surveys
    { key: 'community_surveys_manage', label: 'Surveys', section: 'Surveys', isSectionMaster: true },
    // Reports
    { key: 'community_reports', label: 'Reports' },
  ]},
  { name: 'Equipment', emoji: '🔧', keys: [
    { key: 'equipment_enabled', label: 'Enabled', isMaster: true },
    // View
    { key: 'equipment_view_self_response', label: 'View Assigned', section: 'View', isSectionMaster: true },
    { key: 'equipment_view_all',           label: 'View All',      section: 'View' },
    // Manage
    { key: 'equipment_add',            label: 'Add',       section: 'Manage', isSectionMaster: true },
    { key: 'equipment_edit',           label: 'Edit',      section: 'Manage' },
    { key: 'equipment_delete',         label: 'Delete',    section: 'Manage' },
    { key: 'equipment_export',         label: 'Export',    section: 'Manage' },
    { key: 'equipment_inventory_mode', label: 'Inventory', section: 'Manage' },
    { key: 'equipment_pat_test',       label: 'PAT Test',  section: 'Manage' },
    { key: 'equipment_settings',       label: 'Settings',  section: 'Manage' },
  ]},
  { name: 'Booking', emoji: '📅', keys: [
    { key: 'booking_enabled', label: 'Enabled', isMaster: true },
    { key: 'booking_room_admin', label: 'Manage Rooms' },
  ]},
  { name: 'Finance', emoji: '💰', keys: [
    { key: 'finance_enabled', label: 'Enabled', isMaster: true },
    // Expenses
    { key: 'expenses_view',    label: 'Expenses',      section: 'Expenses',     isSectionMaster: true },
    { key: 'expenses_add',     label: 'Add',           section: 'Expenses' },
    { key: 'expenses_edit',    label: 'Edit',          section: 'Expenses' },
    { key: 'expenses_delete',  label: 'Delete',        section: 'Expenses' },
    { key: 'expenses_approve', label: 'Change Status', section: 'Expenses' },
    // Income
    { key: 'income_view',    label: 'Income',        section: 'Income', isSectionMaster: true },
    { key: 'income_add',     label: 'Add',           section: 'Income' },
    { key: 'income_edit',    label: 'Edit',          section: 'Income' },
    { key: 'income_delete',  label: 'Delete',        section: 'Income' },
    { key: 'income_approve', label: 'Change Status', section: 'Income' },
    // Car Expenses
    { key: 'car_exp_view',    label: 'Car Expenses',  section: 'Car Expenses', isSectionMaster: true },
    { key: 'car_exp_add',     label: 'Add',           section: 'Car Expenses' },
    { key: 'car_exp_edit',    label: 'Edit',          section: 'Car Expenses' },
    { key: 'car_exp_delete',  label: 'Delete',        section: 'Car Expenses' },
    { key: 'car_exp_approve', label: 'Change Status', section: 'Car Expenses' },
    // Payroll
    { key: 'payroll_view',    label: 'Payroll',       section: 'Payroll', isSectionMaster: true },
    { key: 'payroll_add',     label: 'Add',           section: 'Payroll' },
    { key: 'payroll_edit',    label: 'Edit',          section: 'Payroll' },
    { key: 'payroll_delete',  label: 'Delete',        section: 'Payroll' },
    { key: 'payroll_approve', label: 'Change Status', section: 'Payroll' },
    // Fleet (Vehicles + Insurance)
    { key: 'finance_fleet_view',     label: 'Fleet',          section: 'Fleet', isSectionMaster: true },
    { key: 'finance_fleet_add',      label: 'Add',            section: 'Fleet' },
    { key: 'finance_fleet_edit',     label: 'Edit',           section: 'Fleet' },
    { key: 'finance_fleet_delete',   label: 'Delete',         section: 'Fleet' },
    { key: 'finance_fleet_licenses', label: 'View Licenses',  section: 'Fleet' },
    // Create claims on behalf of other employees
    { key: 'finance_create_for_others', label: 'Create for others', section: 'On behalf' },
    // Settings
    { key: 'expenses_settings', label: 'Finance Settings', section: 'Settings' },
  ]},
  { name: 'Orders', emoji: '🛒', keys: [
    { key: 'orders', label: 'Enabled', isMaster: true },
  ]},
  { name: 'Settings', emoji: '⚙️', keys: [
    { key: 'settings_enabled', label: 'Enabled', isMaster: true },
    { key: 'settings_view',    label: 'View Settings' },
    { key: 'history_view',     label: 'View Logs' },
    { key: 'history_view_all', label: 'View All Logs' },
  ]},
  { name: 'Training', emoji: '🎓', keys: [
    { key: 'training_enabled', label: 'Enabled',      isMaster: true },
    { key: 'training_view',    label: 'Take Courses' },
    { key: 'training_admin',   label: 'Admin' },
  ]},
  { name: 'Tasks', emoji: '✅', keys: [
    { key: 'tasks_enabled', label: 'Enabled', isMaster: true },
  ]},
  { name: 'Disk', emoji: '💾', keys: [
    { key: 'disk_enabled', label: 'Enabled',  isMaster: true },
    { key: 'disk_access',  label: 'View / Upload' },
    { key: 'disk_admin',   label: 'Admin (delete)' },
  ]},
  { name: 'HR', emoji: '👥', keys: [
    { key: 'hr_enabled', label: 'Enabled', isMaster: true },
    // Calendar tab
    { key: 'hr_overview_events', label: 'Birthdays & Expiring Docs', section: 'Calendar', isSectionMaster: true },
    // Absence tab
    { key: 'hr_absence_view',         label: 'View Own',            section: 'Absence', isSectionMaster: true },
    { key: 'hr_absence_view_sv',      label: 'View Supervised',     section: 'Absence' },
    { key: 'hr_absence_view_team',    label: 'View Department',     section: 'Absence' },
    { key: 'hr_absence_view_all',     label: 'View All',            section: 'Absence' },
    { key: 'hr_absence_approve',      label: 'Approve Supervised',  section: 'Absence' },
    { key: 'hr_absence_approve_team', label: 'Approve Department',  section: 'Absence' },
    { key: 'hr_absence_approve_all',  label: 'Approve All',         section: 'Absence' },
    // Leave tab
    { key: 'hr_leave_view_own',  label: 'View Own Balance',    section: 'Leave', isSectionMaster: true },
    { key: 'hr_leave_view_sv',   label: 'View Supervised',     section: 'Leave' },
    { key: 'hr_leave_view_dep',  label: 'View Department',     section: 'Leave' },
    { key: 'hr_leave_view_all',  label: 'View All',            section: 'Leave' },
    { key: 'hr_leave_edit_all',  label: 'Edit Adjustments',    section: 'Leave' },
    // Employees tab
    { key: 'hr_employees_enabled', label: 'View All Employees', section: 'Employees', isSectionMaster: true },
    { key: 'users_add',            label: 'Add',                section: 'Employees' },
    { key: 'hr_employees_edit',    label: 'Edit Profiles',      section: 'Employees' },
    { key: 'users_delete',         label: 'Delete',             section: 'Employees' },
    // Departments tab
    { key: 'hr_org_enabled',  label: 'View Departments', section: 'Departments', isSectionMaster: true },
    { key: 'hr_org_edit',     label: 'Edit (drag & drop)', section: 'Departments' },
    // Org Chart tab
    { key: 'hr_org_chart_enabled', label: 'View Org Chart', section: 'Org Chart', isSectionMaster: true },
    // Settings
    { key: 'hr_settings', label: 'Manage Settings', section: 'Settings', isSectionMaster: true },
  ]},
];

// ── Helpers ───────────────────────────────────────────────────────────────────

export const toBool = (v: unknown) =>
  v === true || v === '1' || (typeof v === 'string' && v.toLowerCase() === 'true') ||
  (typeof v === 'number' && v > 0);


// ── Toggle switch (master enable) ────────────────────────────────────────────

export function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <div
      role="switch"
      aria-checked={checked ? 'true' : 'false'}
      title={checked ? 'Section enabled' : 'Section disabled'}
      onClick={disabled ? undefined : onChange}
      style={{
        width: 28, height: 15, borderRadius: 8, flexShrink: 0,
        cursor: disabled ? 'not-allowed' : 'pointer', transition: 'background 0.2s',
        background: checked ? 'var(--color-success)' : 'var(--color-danger)',
        position: 'relative', display: 'inline-block',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div style={{
        width: 11, height: 11, borderRadius: '50%', background: '#fff',
        position: 'absolute', top: 2,
        left: checked ? 15 : 2,
        transition: 'left 0.2s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

// ── Custom checkbox ───────────────────────────────────────────────────────────

export function Check({ checked, onChange, color = 'var(--color-info)', disabled }: { checked: boolean; onChange: () => void; color?: string; disabled?: boolean }) {
  return (
    <div
      role="checkbox"
      aria-checked={checked ? 'true' : 'false'}
      title={checked ? 'Enabled' : 'Disabled'}
      onClick={disabled ? undefined : onChange}
      style={{
        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer', transition: 'background 0.12s, border-color 0.12s',
        opacity: disabled ? 0.5 : 1,
        background: checked ? color : 'transparent',
        border: `1.5px solid ${checked ? color : 'var(--border-new)'}`,
        margin: '0 auto',
      }}
    >
      {checked && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1, fontWeight: 700 }}>✓</span>}
    </div>
  );
}

// ── Numeric selector (0–3: None/Own/Team/All) ─────────────────────────────────

const NUM_LABELS = ['N', 'O', 'T', 'A'];
const NUM_COLORS = ['#9ca3af', '#4f8ef7', '#f4a942', '#43c98a'];
const NUM_TITLES = ['None', 'Own', 'Team', 'All'];

function NumericSelector({ value, onChange, disabled }: { value: number; onChange: (v: number) => void; disabled: boolean }) {
  const cur = typeof value === 'number' ? value : 0;
  return (
    <div style={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
      {NUM_LABELS.map((lbl, i) => (
        <button key={i} type="button" title={NUM_TITLES[i]} onClick={() => !disabled && onChange(i)}
          style={{
            width: 16, height: 16, borderRadius: 3, border: 'none', fontSize: 9, fontWeight: 700, lineHeight: 1,
            cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.3 : 1,
            background: cur === i ? NUM_COLORS[i] : 'var(--bg-input)',
            color: cur === i ? '#fff' : 'var(--text-color-calendar-week)',
            transition: 'background 0.1s',
          }}>{lbl}</button>
      ))}
    </div>
  );
}

// ── Group row ─────────────────────────────────────────────────────────────────

const LABEL_PX = 160;
const COL_PX = 82;

function GroupRow({ group, roles, onToggle, onSetNumeric, canEdit }: {
  group: typeof GROUPS[0]; roles: Role[]; onToggle: (id: number, key: keyof Role) => void; onSetNumeric: (id: number, key: keyof Role, v: number) => void; canEdit: boolean;
}) {
  const [open, setOpen] = useState(false);
  const masterKey = group.keys.find(k => k.isMaster)?.key;
  const subKeys = group.keys.filter(k => !k.isMaster);

  const bd = 'var(--border-input)';

  return (
    <>
      {/* Group header row */}
      <tr
        style={{ background: open ? 'var(--hover-new)' : 'var(--bg-top)', borderTop: '2px solid var(--border-new)' }}
        className="hover:bg-[var(--hover-new)] transition-colors"
      >
        <td
          onClick={() => setOpen(o => !o)}
          style={{ width: LABEL_PX, padding: '9px 14px', cursor: 'pointer', borderBottom: bd, borderRight: bd, borderLeft: bd, position: 'sticky', left: 0, zIndex: 11, background: open ? 'var(--hover-new)' : 'var(--bg-top)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-new)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{group.name}</span>
            <span style={{ fontSize: 9, color: 'var(--text3-new)', transition: 'transform 0.15s', display: 'inline-block', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
          </div>
        </td>
        {roles.map(role => {
          const isMasterOn = masterKey ? toBool(role[masterKey]) : true;
          const cnt = subKeys.reduce((c, k) => (toBool(role[k.key]) ? c + 1 : c), 0);
          return (
            <td key={role.id} style={{ width: COL_PX, padding: '5px 6px', borderBottom: bd, borderRight: bd, textAlign: 'center' }}>
              {masterKey ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Toggle checked={isMasterOn} onChange={() => onToggle(role.id, masterKey)} disabled={!canEdit} />
                  {subKeys.length > 0 && (
                    <span
                      onClick={() => setOpen(o => !o)}
                      style={{ fontSize: 10, color: 'var(--text3-new)', cursor: 'pointer' }}>
                      {cnt}/{subKeys.length}
                    </span>
                  )}
                </div>
              ) : (
                <span
                  onClick={() => setOpen(o => !o)}
                  style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: 'var(--btn-user-form)', color: 'var(--text2-new)', border: bd, cursor: 'pointer' }}>
                  {cnt}/{subKeys.length}
                </span>
              )}
            </td>
          );
        })}
      </tr>

      {/* Expanded: sub-items */}
      {open && (() => {
        let lastSection = '';
        const sectionMasters = Object.fromEntries(
          group.keys.filter(k => k.isSectionMaster).map(k => [k.section, k])
        );
        return group.keys.filter(k => !k.isMaster && !k.isSectionMaster).map((child) => {
          const sectionHeader = child.section && child.section !== lastSection;
          if (sectionHeader) lastSection = child.section!;
          const sectionMasterKey = child.section ? sectionMasters[child.section]?.key : undefined;
          return (
            <React.Fragment key={child.key}>
              {sectionHeader && (
                <tr>
                  <td style={{ padding: '5px 14px 5px 14px', fontSize: 11, fontWeight: 600, color: 'var(--text2-new)', textTransform: 'uppercase', letterSpacing: '0.06em', background: 'var(--surface2)', borderBottom: bd, borderRight: bd, borderLeft: bd, position: 'sticky', left: 0, zIndex: 11 }}>
                    {child.section}
                  </td>
                  {roles.map(role => {
                    const groupMasterOn = masterKey ? toBool(role[masterKey]) : true;
                    const sectionDisabled = !groupMasterOn;
                    const smDef = sectionMasterKey ? sectionMasters[child.section!] : undefined;
                    const isNumericMaster = smDef?.type === 'numeric';
                    return (
                      <td key={role.id} style={{ padding: '3px 5px', background: 'var(--surface2)', borderBottom: bd, borderRight: bd, textAlign: 'center', opacity: sectionDisabled ? 0.3 : 1 }}>
                        {sectionMasterKey
                          ? isNumericMaster
                            ? <NumericSelector value={role[sectionMasterKey] ?? 0} onChange={v => !sectionDisabled && onSetNumeric(role.id, sectionMasterKey as keyof Role, v)} disabled={sectionDisabled || !canEdit} />
                            : <Check checked={toBool(role[sectionMasterKey])} onChange={() => !sectionDisabled && onToggle(role.id, sectionMasterKey as keyof Role)} disabled={sectionDisabled || !canEdit} />
                          : null}
                      </td>
                    );
                  })}
                </tr>
              )}
              <tr key={child.key} style={{ background: 'var(--bg-form)' }} className="hover:bg-[var(--hover-new)] transition-colors">
                <td style={{ padding: child.isMaster ? '6px 14px 6px 14px' : '5px 14px 5px 28px', borderBottom: bd, borderRight: bd, borderLeft: bd, fontSize: 12, fontWeight: child.isMaster ? 700 : 400, color: child.isMaster ? 'var(--text-new)' : 'var(--text2-new)', position: 'sticky', left: 0, zIndex: 11, background: 'var(--bg-form)' }}>
                  {child.label}
                </td>
                {roles.map(role => {
                  const roleMasterOn = masterKey ? toBool(role[masterKey]) : true;
                  const roleSectionOn = sectionMasterKey
                    ? (child.type === 'numeric' || sectionMasters[child.section!]?.type === 'numeric'
                        ? (role[sectionMasterKey] ?? 0) > 0
                        : toBool(role[sectionMasterKey]))
                    : true;
                  const disabled = !roleMasterOn || !roleSectionOn;
                  return (
                    <td key={role.id} style={{ padding: '5px', borderBottom: bd, borderRight: bd, textAlign: 'center', opacity: disabled ? 0.3 : 1 }}>
                      {child.isMaster
                        ? <Toggle checked={toBool(role[child.key])} onChange={() => onToggle(role.id, child.key)} disabled={!canEdit} />
                        : child.type === 'numeric'
                          ? <NumericSelector value={role[child.key] ?? 0} onChange={v => (!disabled && canEdit) && onSetNumeric(role.id, child.key, v)} disabled={disabled || !canEdit} />
                          : <Check checked={toBool(role[child.key])} onChange={() => (!disabled && canEdit) && onToggle(role.id, child.key)} disabled={disabled || !canEdit} />
                      }
                    </td>
                  );
                })}
              </tr>
            </React.Fragment>
          );
        });
      })()}
    </>
  );
}

// ── Main table ────────────────────────────────────────────────────────────────

const RolesTable: React.FC<Props> = ({
  role: initialRoles, onRoleChange, handleShowDelete, showDelete, setShowDelete,
  refreshUserHeder, selectedRoleDelete, handleOpenAdd, refreshRoleAccept, canEdit = false,
}) => {
  const [roles, setRoles] = useState<Role[]>(initialRoles);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [inputWidth, setInputWidth] = useState(0);
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    setRoles(prev => {
      const pIds = prev.map(r => r.id).sort((a, b) => a - b).join(',');
      const nIds = initialRoles.map(r => r.id).sort((a, b) => a - b).join(',');
      return pIds === nIds ? prev : initialRoles;
    });
  }, [initialRoles]);

  useEffect(() => {
    if (spanRef.current) setInputWidth(spanRef.current.offsetWidth + 10);
  }, [editingTitle]);

  const toStored = (b: boolean) => b ? 1 : 0;

  const toggleChild = async (roleId: number, key: keyof Role) => {
    const cur = roles.find(r => r.id === roleId);
    if (!cur) return;
    const newBool = !toBool(cur[key]);
    const expKeys: (keyof Role)[] = ['expenses_user', 'expenses_manager', 'expenses_admin'];
    let updated = { ...cur };
    let payload: any = {};

    if (expKeys.includes(key)) {
      expKeys.forEach(k => { (updated as any)[k] = 0; payload[k] = false; });
      if (newBool) { (updated as any)[key] = 1; payload[key] = true; }
    } else {
      (updated as any)[key] = toStored(newBool);
      payload[key] = newBool;
    }

    setRoles(prev => prev.map(r => r.id === roleId ? updated : r));
    try {
      await axios.put(`/roles/${roleId}`, payload);
      refreshRoleAccept?.();
      onRoleChange?.();
    } catch {
      setRoles(prev => prev.map(r => r.id === roleId ? cur : r));
    }
  };

  const setNumeric = async (roleId: number, key: keyof Role, value: number) => {
    const cur = roles.find(r => r.id === roleId);
    if (!cur) return;
    const updated = { ...cur, [key]: value };
    setRoles(prev => prev.map(r => r.id === roleId ? updated : r));
    try {
      await axios.put(`/roles/${roleId}`, { [key]: value });
      refreshRoleAccept?.();
      onRoleChange?.();
    } catch {
      setRoles(prev => prev.map(r => r.id === roleId ? cur : r));
    }
  };

  const saveTitle = async (roleId: number) => {
    if (!editingTitle.trim()) return;
    const old = roles.find(r => r.id === roleId)?.title || '';
    setRoles(prev => prev.map(r => r.id === roleId ? { ...r, title: editingTitle } : r));
    setEditingId(null);
    try {
      await axios.put(`/roles/${roleId}`, { title: editingTitle });
      onRoleChange?.();
    } catch {
      setRoles(prev => prev.map(r => r.id === roleId ? { ...r, title: old } : r));
    }
  };

  const saveColor = async (roleId: number, color: string) => {
    const old = roles.find(r => r.id === roleId)?.color;
    setRoles(prev => prev.map(r => r.id === roleId ? { ...r, color } : r));
    try {
      await axios.put(`/roles/${roleId}`, { color });
      onRoleChange?.();
      refreshUserHeder?.();
    } catch {
      setRoles(prev => prev.map(r => r.id === roleId ? { ...r, color: old } : r));
    }
  };

  const sorted = [...roles].sort((a, b) => a.id - b.id);

  const tableMinW = LABEL_PX + COL_PX * sorted.length;
  const bd = 'var(--border-input)';
  const bdNew = '1px solid var(--border-new)';

  return (
    <div style={{ marginBottom: 35 }}>
      <div style={{
        overflowX: 'auto',
        borderRadius: 'var(--radius-card)',
        border: bdNew,
        background: 'var(--surface)',
        backdropFilter: 'blur(20px) saturate(150%)',
        WebkitBackdropFilter: 'blur(20px) saturate(150%)',
        boxShadow: '0 2px 10px var(--shadow-new)',
      }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', minWidth: tableMinW }}>
          {/* Header */}
          <thead>
            <tr style={{ background: 'var(--surface2)', borderBottom: bdNew }}>
              <th aria-label="Section" style={{ width: LABEL_PX, padding: '10px 14px', borderBottom: bdNew, borderRight: bd, borderTop: bdNew, borderLeft: bdNew, position: 'sticky', top: 0, left: 0, zIndex: 22, background: 'var(--surface2)' }} />
              {sorted.map(role => (
                <th key={role.id} style={{ width: COL_PX, padding: '8px 6px', textAlign: 'center', borderBottom: bdNew, borderRight: bd, position: 'sticky', top: 0, zIndex: 20, background: 'var(--surface2)' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }} className="group">
                    {/* Title + delete */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
                      {editingId === role.id ? (
                        <>
                          <input type="text" title={t('EditTitle')} placeholder={role.title} value={editingTitle}
                            onChange={e => setEditingTitle(e.target.value)}
                            onBlur={() => saveTitle(role.id)}
                            onKeyDown={e => { if (e.key === 'Enter') saveTitle(role.id); if (e.key === 'Escape') setEditingId(null); }}
                            autoFocus
                            style={{ fontSize: 12, width: inputWidth || 70, minWidth: 30, padding: '0 2px', border: 'none', borderBottom: bdNew, background: 'transparent', color: 'var(--text-new)', textAlign: 'center', outline: 'none' }}
                          />
                          <span ref={spanRef} style={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'pre', fontSize: 12 }}>{editingTitle || ' '}</span>
                        </>
                      ) : (
                        canEdit ? (
                          <Tooltip title={t('EditTitle')}>
                            <span
                              onClick={() => { setEditingId(role.id); setEditingTitle(role.title); }}
                              style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-new)', cursor: 'pointer', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {role.title}
                            </span>
                          </Tooltip>
                        ) : (
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-new)', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {role.title}
                          </span>
                        )
                      )}
                      {canEdit && (
                        <Tooltip title={t('DeleteRole')}>
                          <button type="button" aria-label={t('DeleteRole')}
                            onClick={e => { e.stopPropagation(); handleShowDelete(role.id); }}
                            className="opacity-0 group-hover:opacity-60 hover:!opacity-100"
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: 'var(--color-danger)', display: 'flex', flexShrink: 0, transition: 'opacity 0.15s' }}>
                            <CloseIcon style={{ fontSize: 11 }} />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {GROUPS.map(group => (
              <GroupRow key={group.name} group={group} roles={sorted} onToggle={toggleChild} onSetNumeric={setNumeric} canEdit={canEdit} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className='absolute bottom-0 left-0 py-[5px] flex justify-between bg-[var(--bg-top-fix)] w-full items-center rounded-b-[5px] px-[20px]'>
        {canEdit ? (
          <Tooltip title={t('CreateRole')} placement="right">
            <Fab size="small" color="primary" aria-label="add"
              sx={{ width: 25, height: 23, minHeight: 'auto', boxShadow: 'none' }}
              onClick={() => handleOpenAdd()}>
              <AddIcon sx={{ color: 'white !important' }} />
            </Fab>
          </Tooltip>
        ) : <div />}
        <div className="flex gap-[5px] items-center text-[16px]">
          <span className='opacity-70 text-[13px]'>{sorted.length}</span>
          <FontAwesomeIcon icon={faUsersLine} className='text-[16px] opacity-30 text-[var(--text-color)]' />
        </div>
      </div>
    </div>
  );
};

export default RolesTable;
